import * as Sentry from "@sentry/nextjs";
import firebase from "~/firebase/clientApp";
import { useEffect, useState } from "react";
import Page from "~/components/Page";
import { useRouter } from "next/router";
import {
  validateEmailLink,
  getOtpLocalEmail,
  getOtpLocalDisplayName,
  sendUserDetailsToBackend,
} from "~/actions/AuthActions";
import {
  Box,
  Flex,
  Stack,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormErrorMessage,
  Button,
  useToast,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { Display03, Label01 } from "~/components/Texts";
import Spinner from "~/components/animations/Spinner";
import { Formik, Field } from "formik";
import { validateEmail, validateUsername } from "~/utils/InputValidators";
import { MdEmail } from "react-icons/md";
import { FaUserAlt } from "react-icons/fa";
import { useTheme } from "@chakra-ui/react";
import Logo from "~/components/logo";

const SIGNUP_SUCCESS_REDIRECT = "/onboarding";
const LOGIN_SUCCESS_REDIRECT = "/my-shops";
const ERROR_REDIRECT = "/auth/login";

export default function ConfirmEmail() {
  const router = useRouter();
  const toast = useToast();

  const [isValidating, setValidating] = useState(false);
  const [displayName, setDisplayName] = useState(getOtpLocalDisplayName());
  const [email, setEmail] = useState(getOtpLocalEmail());
  const [user, setUser] = useState();
  const [isNewUser, setIsNewUser] = useState(false);

  useEffect(() => {
    if (!firebase.auth().isSignInWithEmailLink(window.location.href))
      router.replace(ERROR_REDIRECT);
  }, []);

  useEffect(() => {
    if (email) doLogIn(email);
  }, [email]);

  useEffect(() => {
    if (isNewUser && displayName && user) initializeUserOnBackend();
  }, [isNewUser, displayName, user]);

  function doLogIn(email) {
    setValidating(true);

    validateEmailLink(email, window.location.href)
      .then((result) => {
        if (result.additionalUserInfo.isNewUser) {
          setIsNewUser(true);
          setUser(result.user);
        } else router.replace(LOGIN_SUCCESS_REDIRECT);
      })
      .catch((error) => onError(error))
      .finally(() => setValidating(false));
  }

  function initializeUserOnBackend() {
    const userInfo = {
      userId: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,
      displayName: displayName,
      avatarUrl: user.photoURL,
      locale: "es",
      provider: "email",
      googleAccessToken: null,
      facebookAccessToken: null,
      twitterAccessToken: null,
    };

    setValidating(true);
    sendUserDetailsToBackend(userInfo)
      .then(() => router.replace(SIGNUP_SUCCESS_REDIRECT))
      .catch((error) => onError(error))
      .finally(() => setValidating(false));
  }

  function onError(error) {
    showErrorToast();
    Sentry.captureException(error);
    router.replace(ERROR_REDIRECT);
  }

  function showErrorToast() {
    toast({
      title: "Error de autenticación",
      description: "No es posible inciar sesión, por favor vuelve a intentarlo",
      status: "error",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  }

  return (
    <Page title="Iniciando sesión... | Shop Story">
      <VStack
        w="100%"
        h="100%"
        px="4"
        py="8"
        backgroundColor={useColorModeValue(
          "light.background.superLite",
          "dark.background.superLite"
        )}
      >
        <VStack w="100%" h="100%" flex="1" justify="center" spacing="4">
          {!isValidating && !email && (
            <EmailPrompt
              onSubmit={(values, actions) => setEmail(values.email)}
            />
          )}
          {!isValidating && !displayName && isNewUser && (
            <DisplayNamePrompt
              onSubmit={(values, actions) => setDisplayName(values.username)}
            />
          )}
          {isValidating && <LogginIn />}
        </VStack>
        <Logo />
      </VStack>
    </Page>
  );
}

function EmailPrompt({ onSubmit = () => {} }) {
  const chakraTheme = useTheme();

  return (
    <Box maxWidth="500px" width="100%" p="6">
      <Stack>
        <Label01>Confirmá tu dirección de correo</Label01>
        <Formik
          initialValues={{}}
          onSubmit={(values, actions) => {
            actions.setSubmitting(true);
            onSubmit(values, actions);
          }}
        >
          {(props) => (
            <form onSubmit={props.handleSubmit} style={{ width: "100%" }}>
              <Stack spacing="4" pt="4">
                <Field name="email" validate={validateEmail}>
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.email && form.touched.email}
                    >
                      <InputGroup size="lg">
                        <InputLeftElement
                          children={
                            <MdEmail
                              color={useColorModeValue(
                                chakraTheme.colors.light.brand.strong,
                                chakraTheme.colors.dark.brand.strong
                              )}
                            />
                          }
                        />
                        <Input
                          {...field}
                          type="email"
                          placeholder="Dirección de correo"
                        />
                      </InputGroup>
                      <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Flex justify="flex-end" width="100%">
                  <Button type="submit" size="lg" variant="solid">
                    Confirmar
                  </Button>
                </Flex>
              </Stack>
            </form>
          )}
        </Formik>
      </Stack>
    </Box>
  );
}

function DisplayNamePrompt({ onSubmit = () => {} }) {
  const chakraTheme = useTheme();

  return (
    <Box maxWidth="500px" width="100%" p="6">
      <Stack>
        <Label01>Ingresá tu nombre</Label01>
        <Formik
          initialValues={{}}
          onSubmit={(values, actions) => {
            actions.setSubmitting(true);
            onSubmit(values, actions);
          }}
        >
          {(props) => (
            <form onSubmit={props.handleSubmit} style={{ width: "100%" }}>
              <Stack spacing="4" pt="4">
                <Field name="username" validate={validateUsername}>
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.username && form.touched.username}
                    >
                      <InputGroup size="lg">
                        <InputLeftElement
                          children={
                            <FaUserAlt
                              color={useColorModeValue(
                                chakraTheme.colors.light.brand.strong,
                                chakraTheme.colors.dark.brand.strong
                              )}
                            />
                          }
                        />
                        <Input {...field} type="text" placeholder="Nombre" />
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.username}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Flex justify="flex-end" width="100%">
                  <Button type="submit" size="lg" variant="solid">
                    Confirmar
                  </Button>
                </Flex>
              </Stack>
            </form>
          )}
        </Formik>
      </Stack>
    </Box>
  );
}

function LogginIn() {
  return (
    <Stack>
      <Flex width="100%" align="center" justify="center">
        <Display03>Iniciando sesión</Display03>
      </Flex>
      <Spinner height={300} width={300} />
    </Stack>
  );
}
